import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { FaWhatsapp } from 'react-icons/fa';
import './LegalPage.css';

const WhatsappIcon = FaWhatsapp as unknown as React.FC<{
  'aria-hidden'?: boolean;
  style?: React.CSSProperties;
}>;

const whatsappLink = (
  <a href="https://wa.me/919220891200" target="_blank" rel="noopener noreferrer">
    <WhatsappIcon aria-hidden style={{ color: '#25D366', verticalAlign: '-0.15em' }} /> +91
    92208 91200
  </a>
);

const Terms: React.FC = () => {
  return (
    <div className="legal-page">
      <Helmet>
        <title>Terms and Conditions — ViralAI</title>
        <meta name="description" content="ViralAI terms and conditions — the rules for using our AI video-to-shorts platform." />
        <link rel="canonical" href="https://www.vimapi.com/terms" />
        <meta property="og:url" content="https://www.vimapi.com/terms" />
        <meta property="og:title" content="Terms and Conditions — ViralAI" />
        <meta property="og:description" content="ViralAI terms and conditions — the rules for using our AI video-to-shorts platform." />
      </Helmet>
      <nav className="legal-nav">
        <Link to="/" className="legal-brand" aria-label="ViralAI home">
          <img src="/viralai-logo.svg" alt="VimAPI — A ViralAI Platform" className="legal-logo" />
        </Link>
        <Link to="/" className="back-link">← Back to Home</Link>
      </nav>

      <div className="legal-content">
        <h1>Terms and Conditions</h1>
        <p className="effective-date">
          <strong>Effective Date:</strong> September 9, 2026 &nbsp;·&nbsp; <strong>Last Updated:</strong> September 9, 2026
        </p>

        <p>
          Welcome, and thank you for your interest in ViralAI.Tech (“ViralAI.Tech,” “we,” or “us”)
          and our websites at <a href="https://www.vimapi.com">https://www.vimapi.com</a> and{' '}
          <a href="https://www.viralai.tech">https://www.viralai.tech</a>, along with our related
          websites, networks, applications, APIs, and other services provided by us (collectively,
          the “Service”). These Terms of Service are a legally binding contract between you and
          ViralAI.Tech regarding your use of the Service.
        </p>

        <h2>YouTube Terms of Service</h2>
        <p>
          Please <a href="https://www.youtube.com/t/terms" target="_blank" rel="noopener noreferrer">click here</a>{' '}
          to read about YouTube’s Terms of Service. By using the YouTube API clients, users are
          agreeing to be bound by the YouTube Terms of Service.
        </p>

        <section className="intro-notice">
          <p><strong>PLEASE READ THE FOLLOWING TERMS CAREFULLY</strong></p>
          <p>
            BY CLICKING “I ACCEPT,” OR BY DOWNLOADING, INSTALLING, OR OTHERWISE ACCESSING OR USING
            THE SERVICE, YOU AGREE THAT YOU HAVE READ AND UNDERSTOOD, AND, AS A CONDITION TO YOUR
            USE OF THE SERVICE, YOU AGREE TO BE BOUND BY, THE FOLLOWING TERMS AND CONDITIONS,
            INCLUDING ViralAI.Tech’S PRIVACY POLICY AND ANY ADDITIONAL TERMS AND POLICIES
            ViralAI.Tech MAY PROVIDE FROM TIME TO TIME (TOGETHER, THESE “TERMS”). If you are not
            eligible, or do not agree to the Terms, then you do not have our permission to use the
            Service. YOUR USE OF THE SERVICE, AND ViralAI.Tech’S PROVISION OF THE SERVICE TO YOU,
            CONSTITUTES AN AGREEMENT BY ViralAI.Tech AND BY YOU TO BE BOUND BY THESE TERMS.
          </p>
          <p>
            Please read our <Link to="/privacy">Privacy Policy</Link> carefully for information
            relating to our collection, use, storage, disclosure of your personal information. The
            Privacy Policy is incorporated by this reference into, and made a part of, these Terms.
          </p>
        </section>

        <section className="intro-notice">
          <p><strong>ARBITRATION NOTICE</strong></p>
          <p>
            Except for certain kinds of disputes described in Section 16, you agree that disputes
            arising under these Terms will be resolved by binding, individual arbitration, and BY
            ACCEPTING THESE TERMS, YOU AND ViralAI.Tech ARE EACH WAIVING THE RIGHT TO A TRIAL BY
            JURY OR TO PARTICIPATE IN ANY CLASS ACTION OR REPRESENTATIVE PROCEEDING. YOU AGREE TO
            GIVE UP YOUR RIGHT TO GO TO COURT to assert or defend your rights under this contract
            (except for matters that may be taken to small claims court). Your rights will be
            determined by a NEUTRAL ARBITRATOR and NOT a judge or jury. (See Section 16).
          </p>
        </section>

        <section>
          <h2>1. Service Overview</h2>
          <p>VimAPI is a social media management platform that enables you to:</p>
          <ul>
            <li>Schedule and publish content to multiple social media platforms</li>
            <li>Manage video content across YouTube, Facebook, Instagram, Twitter, and LinkedIn</li>
            <li>Access analytics and insights about your content performance</li>
            <li>Create and edit content using our tools</li>
          </ul>
        </section>

        <section>
          <h2>2. Eligibility</h2>
          <p>You must be at least <strong>18 years old</strong> to use the Service.</p>
          <p>By using the Service, you represent and warrant that:</p>
          <ul>
            <li>You are at least 18 years old</li>
            <li>You have not been previously suspended or banned from the Service</li>
            <li>Your use complies with all applicable laws and regulations</li>
            <li>If registering on behalf of an organization, you have authority to bind that entity to these Terms</li>
          </ul>
        </section>

        <section>
          <h2>3. Account Registration</h2>
          <p>To access most features, you must create an account. You agree to:</p>
          <ul>
            <li>Provide accurate and complete information</li>
            <li>Keep your account information up to date</li>
            <li>Maintain the confidentiality of your password</li>
            <li>Accept responsibility for all activities under your account</li>
            <li>Notify us immediately if you suspect unauthorized access</li>
          </ul>
          <p>You are solely responsible for maintaining the security of your account.</p>
        </section>

        <section>
          <h2>4. General Payment Terms</h2>
          <p>
            Premium features of the Service will require you to pay fees upon registering for the
            applicable premium service. Before you pay any fees, you will have an opportunity to
            review and accept the fees that you will be charged. All fees are in U.S. Dollars and
            are non-refundable. Fees vary based on the plan, with different pricing schemes for
            individual users and organizations.
          </p>

          <h3>4.1 Price</h3>
          <p>
            ViralAI.Tech reserves the right to determine pricing for the Service. ViralAI.Tech will
            make reasonable efforts to keep pricing information published on the website up to
            date. We encourage you to check our website periodically for current pricing
            information, located here: <a href="https://viralai.tech/pricing">viralai.tech/pricing</a>.
            ViralAI.Tech may change the fees for any feature of the Service, including additional
            fees or charges, if ViralAI.Tech gives you advance notice of changes before they apply.
            ViralAI.Tech, at its sole discretion, may make promotional offers with different
            features and different pricing to any of ViralAI.Tech’s customers. These promotional
            offers, unless made to you, will not apply to your offer or these Terms.
          </p>

          <h3>4.2 Authorization</h3>
          <p>
            You authorize ViralAI.Tech to charge all sums for the orders that you make and any
            level of Service you select as described in these Terms or published by ViralAI.Tech,
            to the payment method specified in your account. If you pay any fees with a credit
            card, ViralAI.Tech may seek pre-authorization of your credit card account prior to your
            purchase to verify that the credit card is valid and has the necessary funds or credit
            available to cover your purchase.
          </p>

          <h3>4.3 Subscription Service and Cancellation Policy</h3>
          <p>
            The Service may include automatically recurring payments for periodic charges
            (“Subscription Service”). If you activate a Subscription Service, you authorize
            ViralAI.Tech to periodically charge, on a going-forward basis and until cancellation of
            either the recurring payments or your account, all accrued sums on or before the
            payment due date for the accrued sums. The “Subscription Billing Date” is the date when
            you purchase your first subscription to the Service. For information on the
            “Subscription Fee”, please see our Pricing page. Your account will be charged
            automatically on the Subscription Billing Date all applicable fees for the next
            subscription period. The subscription will continue unless and until you cancel your
            subscription or we terminate it. You must cancel your subscription before it renews in
            order to avoid billing of the next periodic Subscription Fee to your account. We will
            bill the periodic Subscription Fee to the payment method you provide to us during
            registration (or to a different payment method if you change your payment information).
          </p>

          <h3>4.4 Delinquent Accounts</h3>
          <p>
            ViralAI.Tech may suspend or terminate access to the Service for any account for which
            any amount is due but unpaid. In addition to the amount due for the Service, a
            delinquent account will be charged with fees or charges that are incidental to any
            chargeback or collection of the unpaid amount, including collection fees.
          </p>
        </section>

        <section>
          <h2>5. Licenses</h2>

          <h3>5.1 Permission to Use</h3>
          <p>
            Subject to your complete and ongoing compliance with these Terms, ViralAI.Tech grants
            you limited, non-transferable, non-sublicensable, revocable permission to access and
            use the Service for your personal, internal use during the Term at the level of service
            for which you have paid all applicable Fees.
          </p>

          <h3>5.2 Restrictions</h3>
          <p>
            Except and solely to the extent such a restriction is impermissible under applicable
            law, you may not: (a) reproduce, distribute, publicly display, or publicly perform the
            Service; (b) make modifications to the Service; or (c) interfere with or circumvent any
            feature of the Service, including any security or access control mechanism. If you are
            prohibited under applicable law from using the Service, you may not use it. You may not
            use the Service on behalf of any third party, or in a service bureau or similar
            capacity.
          </p>

          <h3>5.3 Feedback</h3>
          <p>
            If you choose to provide input and suggestions regarding problems with or proposed
            modifications or improvements to the Service (“Feedback”), then you hereby grant
            ViralAI.Tech an unrestricted, perpetual, irrevocable, non-exclusive, fully-paid,
            royalty-free right to exploit the Feedback in any manner and for any purpose, including
            to improve the Service and create other products and services.
          </p>

          <h3>5.4 Demo Videos</h3>
          <p>
            Any demo video shared with you through the Service is provided only to show the
            capabilities of the Service. You must not use any demo video for professional or
            commercial purposes. You are solely responsible for any issue, claim, loss, or damage
            arising from your use of a demo video, and Section 13 (Indemnity) applies to that use.
          </p>
        </section>

        <section>
          <h2>6. Ownership; Proprietary Rights</h2>
          <p>
            The Service is owned and operated by ViralAI.Tech. The visual interfaces, graphics,
            design, compilation, information, data, computer code (including source code or object
            code), products, software, services, templates, and all other elements of the Service
            (“Materials”) provided by ViralAI.Tech are protected by intellectual property and other
            laws. All Materials included in the Service are the property of ViralAI.Tech or its
            third party licensors. Except as expressly authorized by ViralAI.Tech, you may not make
            use of the Materials. ViralAI.Tech reserves all rights to the Materials not granted
            expressly in these Terms.
          </p>
        </section>

        <section>
          <h2>7. Third Party Terms</h2>

          <h3>7.1 Third Party Services and Linked Websites</h3>
          <p>
            ViralAI.Tech provides tools through the Service that enable you to import and export
            information, including User Content, to and from third party services, including
            through features that allow you to link your account on ViralAI.Tech with an account on
            a third party social network service, such as X or Facebook. By using one of these
            tools, you agree that ViralAI.Tech may transfer that information to and from the
            applicable third party service. Third party services are not under ViralAI.Tech’s
            control, and ViralAI.Tech is not responsible for any third party service’s use of your
            exported information. The Service may also contain links to third party websites.
            Linked websites are not under ViralAI.Tech’s control, and ViralAI.Tech is not
            responsible for their content.
          </p>

          <h3>7.2 Third Party Software</h3>
          <p>
            The Service may include or incorporate third party software components that are
            generally available free of charge under licenses granting recipients broad rights to
            copy, modify, and distribute those components (“Third Party Components”). Although the
            Service is provided to you subject to these Terms, nothing in these Terms prevents,
            restricts, or is intended to prevent or restrict you from obtaining Third Party
            Components under the applicable third party licenses or to limit your use of Third
            Party Components under those third party licenses.
          </p>
        </section>

        <section>
          <h2>8. User Content</h2>

          <h3>8.1 User Content Generally</h3>
          <p>
            Certain features of the Service may permit users to upload content to the Service,
            including social media posts and other content which may be comprised of messages,
            reviews, photos, video, images, data, text, and other types of works (“User Content”)
            and to publish User Content on the Service. You retain any copyright and other
            proprietary rights that you may hold in the User Content that you post to the Service.
            Nevertheless, we need certain permission from you in order to provide the Service.
          </p>

          <h3>8.2 Limited License Grant to ViralAI.Tech</h3>
          <p>
            By providing User Content to or via the Service, you grant ViralAI.Tech a worldwide,
            non-exclusive, royalty-free, fully paid right and license (with the right to
            sublicense) to host, store, transfer, display, perform, reproduce, modify for the
            purpose of formatting for display, and distribute your User Content, in whole or in
            part, in any media formats and through any media channels now known or hereafter
            developed.
          </p>

          <h3>8.3 Limited License Grant to Other Users</h3>
          <p>
            By providing User Content to or via the Service to other users of the Service, you
            grant those users a non-exclusive license to access and use that User Content as
            permitted by these Terms and the functionality of the Service.
          </p>

          <h3>8.4 User Content Representations and Warranties</h3>
          <p>
            ViralAI.Tech disclaims any and all liability in connection with User Content. You are
            solely responsible for your User Content and the consequences of providing User Content
            via the Service. By providing User Content via the Service, you affirm, represent, and
            warrant that:
          </p>
          <ol type="a">
            <li>
              you are the creator and owner of the User Content, or have the necessary licenses,
              rights, consents, and permissions to authorize ViralAI.Tech and users of the Service
              to use and distribute your User Content as necessary to exercise the licenses granted
              by you in this Section, in the manner contemplated by ViralAI.Tech, the Service, and
              these Terms;
            </li>
            <li>
              your User Content, and the use of your User Content as contemplated by these Terms,
              does not and will not: (i) infringe, violate, or misappropriate any third party
              right, including any copyright, trademark, patent, trade secret, moral right, privacy
              right, right of publicity, or any other intellectual property or proprietary right;
              (ii) slander, defame, libel, or invade the right of privacy, publicity or other
              property rights of any other person; or (iii) cause ViralAI.Tech to violate any law
              or regulation; and
            </li>
            <li>
              your User Content could not be deemed by a reasonable person to be objectionable,
              profane, indecent, pornographic, harassing, threatening, embarrassing, hateful, or
              otherwise inappropriate.
            </li>
            <li>
              your User Content does not and will not contain Hateful Content, a Threat of Physical
              Harm, or Harassment
            </li>
          </ol>
          <p>
            The following serves as a guide to help illustrate generally the types of content that
            fall within the scope of ViralAI.Tech’s policy on Hateful Content, Threats of Physical
            Harm, and Harassment, but is not exhaustive. This Section 8.4(d) does not limit any of
            our other rights or remedies provided herein. For the avoidance of doubt, your User
            Content may not include, and ViralAI.Tech may remove or refuse to publish or promote
            any User Content that violates the terms or policies of any third party platform with
            which ViralAI.Tech’s Services integrate or interoperate.
          </p>

          <p><strong>Hateful Content includes:</strong></p>
          <p>
            Any statement, image, photograph, or other content that in our sole judgment could be
            reasonably perceived to harm, threaten, demean, promote the harassment of, promote the
            intimidation of, or promote the abuse of others for any reason, including by reason of
            race, gender or gender identity, national origin, sexual orientation, religion, or
            otherwise.
          </p>

          <p><strong>A Threat of Physical Harm includes:</strong></p>
          <p>
            Any statement, photograph, advertisement, or other content that in our sole judgment
            could be reasonably perceived to threaten, advocate, or incite physical harm to or
            violence against others, including references to current or historical figures or
            groups that are known for purporting such content, such as the Ku Klux Klan, Nazi
            Party, and the like.
          </p>

          <p><strong>Harassment includes:</strong></p>
          <ul>
            <li>Revealing someone’s personal information, also known as “doxxing”.</li>
            <li>Online stalking, and bullying.</li>
            <li>Wishes for physical harm directed at a person or persons.</li>
            <li>Incitement of others to any of the previous items.</li>
          </ul>

          <p>
            We reserve the right to suspend or terminate accounts and remove individual posts which
            contain Hateful Content, a Threat of Physical Harm, or Harassment.
          </p>
          <p>We also may suspend or terminate your account if we determine, in our sole discretion, that you are either:</p>
          <ul>
            <li>
              An organization which has publicly stated or acknowledged that its goals, objectives,
              positions, or founding tenets include statements or principles that could be
              reasonably perceived to advocate, encourage, or sponsor Hateful Content, Harassment,
              or A Threat of Physical Harm.
            </li>
            <li>
              A person or organization that has acted in such a way as could be reasonably
              perceived to support, condone, encourage, or represent Hateful Content, Harassment,
              or A Threat of Physical Harm.
            </li>
          </ul>
          <p>
            Notwithstanding the foregoing, we reserve the right to screen, remove, edit, or block
            any User Content we find in violation of the Terms or that we find, in our sole
            discretion to be otherwise objectionable, at our sole discretion.
          </p>

          <h3>8.5 User Content Disclaimer</h3>
          <p>
            We are under no obligation to edit or control User Content that you or other users post
            or publish, and will not be in any way responsible or liable for User Content.
            ViralAI.Tech may, however, at any time and without prior notice, screen, remove, edit,
            or block any User Content that in our sole judgment violates these Terms or is
            otherwise objectionable. You understand that when using the Service you will be exposed
            to User Content from a variety of sources and acknowledge that User Content may be
            inaccurate, offensive, indecent, or objectionable. You agree to waive, and do waive,
            any legal or equitable right or remedy you have or may have against ViralAI.Tech with
            respect to User Content. If notified by a user or content owner that User Content
            allegedly does not conform to these Terms, we may investigate the allegation and
            determine in our sole discretion whether to remove the User Content, which we reserve
            the right to do at any time and without notice. For clarity, ViralAI.Tech does not
            permit copyright-infringing activities on the Service.
          </p>

          <h3>8.6 Monitoring Content</h3>
          <p>
            ViralAI.Tech does not control and does not have any obligation to monitor: (a) User
            Content; (b) any content made available by third parties; or (c) the use of the Service
            by its users. You acknowledge and agree that ViralAI.Tech reserves the right to, and
            may from time to time, monitor any and all information transmitted or received through
            the Service for operational and other purposes. If at any time ViralAI.Tech chooses to
            monitor the content, ViralAI.Tech still assumes no responsibility or liability for
            content or any loss or damage incurred as a result of the use of content. During
            monitoring, information may be examined, recorded, copied, and used in accordance with
            our <Link to="/privacy">Privacy Policy</Link>.
          </p>
        </section>

        <section>
          <h2>9. Prohibited Conduct</h2>
          <p><strong>BY USING THE SERVICE YOU AGREE NOT TO:</strong></p>
          <ul>
            <li>use the Service for any illegal purpose or in violation of any local, state, national, or international law;</li>
            <li>
              violate, or encourage others to violate, any right of a third party, including by
              infringing or misappropriating any third party intellectual property right;
            </li>
            <li>
              interfere with security-related features of the Service, including by: (i) disabling
              or circumventing features that prevent or limit use or copying of any content; (ii)
              reverse engineering or otherwise attempting to discover the source code of any
              portion of the Service except to the extent that the activity is expressly permitted
              by applicable law; or (iii) hacking, password “mining” or using any other
              illegitimate means of interference;
            </li>
            <li>modify or create derivatives of any part of the Service;</li>
            <li>
              interfere with the operation of the Service or any user’s enjoyment of the Service,
              including by: (i) uploading or otherwise disseminating any virus, adware, spyware,
              worm, or other malicious code; (ii) making any unsolicited offer or advertisement to
              another user of the Service; (iii) collecting personal information about another user
              or third party without consent; or (iv) interfering with or disrupting any network,
              equipment, or server connected to or used to provide the Service;
            </li>
            <li>
              perform any fraudulent activity including impersonating any person or entity,
              claiming a false affiliation, accessing any other Service account without permission,
              or falsifying your age or date of birth;
            </li>
            <li>
              take action that imposes an unreasonable or disproportionately large load on the
              infrastructure of the Service of ViralAI.Tech’s systems or networks, or any systems
              or networks connected to the Service or ViralAI.Tech;
            </li>
            <li>
              sell or otherwise transfer the access granted under these Terms or any Materials (as
              defined in Section 6) or any right or ability to view, access, or use any Materials;
              or
            </li>
            <li>
              attempt to do any of the acts described in this Section 9 or assist or permit any
              person in engaging in any of the acts described in this Section 9.
            </li>
          </ul>
        </section>

        <section>
          <h2>10. Digital Millennium Copyright Act</h2>

          <h3>10.1 DMCA Notification</h3>
          <p>
            We comply with the provisions of the Digital Millennium Copyright Act applicable to
            Internet service providers (17 U.S.C. §512, as amended).
          </p>
          <p>
            Any notice alleging that materials hosted by or distributed through the Service
            infringe intellectual property rights must include the following information:
          </p>
          <ol type="a">
            <li>an electronic or physical signature of the person authorized to act on behalf of the owner of the copyright or other right being infringed;</li>
            <li>a description of the copyrighted work or other intellectual property that you claim has been infringed;</li>
            <li>a description of the material that you claim is infringing and where it is located on the Service;</li>
            <li>your address, telephone number, and email address;</li>
            <li>
              a statement by you that you have a good faith belief that the use of the materials on
              the Service of which you are complaining is not authorized by the copyright owner,
              its agent, or the law; and
            </li>
            <li>
              a statement by you that the above information in your notice is accurate and that,
              under penalty of perjury, you are the copyright or intellectual property owner or
              authorized to act on the copyright or intellectual property owner’s behalf.
            </li>
          </ol>

          <h3>10.2 Repeat Infringers</h3>
          <p>
            It is ViralAI.Tech’s policy to promptly terminate the accounts of users that are
            determined by ViralAI.Tech to be repeat infringers.
          </p>
        </section>

        <section>
          <h2>11. Modification of these Terms</h2>
          <p>
            We reserve the right to change these Terms on a going-forward basis at any time. Please
            check these Terms periodically for changes. Modifications will become effective upon
            the earlier of (a) your acceptance of the modified Terms, (b) your use of the Service
            with actual knowledge of the modified Terms, or (c) thirty (30) days following our
            publication of the modified Terms through the Service. Except as expressly permitted in
            this Section 11, these Terms may be amended only by a written agreement signed by
            authorized representatives of the parties to these Terms. Disputes arising under these
            Terms will be resolved in accordance with the version of these Terms that was in effect
            at the time the dispute arose.
          </p>
        </section>

        <section>
          <h2>12. Term, Termination and Modification of the Service</h2>

          <h3>12.1 Term</h3>
          <p>
            These Terms are effective beginning when you accept the Terms or first download,
            install, access, or use the Service, and ending when terminated as described in Section
            12.2.
          </p>

          <h3>12.2 Termination</h3>
          <p>
            If you violate any provision of these Terms, your authorization to access the Service
            and these Terms automatically terminate. In addition, ViralAI.Tech may, at its sole
            discretion, terminate these Terms or your account on the Service, or suspend or
            terminate your access to the Service, at any time for any reason or no reason, with or
            without notice. You may terminate your account and these Terms at any time as provided
            in Section 4.3 or by contacting customer service at{' '}
            <a href="mailto:support@vimapi.com">support@vimapi.com</a>.
          </p>

          <h3>12.3 Effect of Termination</h3>
          <p>
            Upon termination of these Terms: (a) your license rights will terminate and you must
            immediately cease all use of the Service; (b) you will no longer be authorized to
            access your account or the Service; (c) you must pay ViralAI.Tech any unpaid amount
            that was due prior to termination; and (d) all payment obligations accrued prior to
            termination and Sections 5.3, 6, 12.3, 13, 14, 15, 16 and 17 will survive.
          </p>

          <h3>12.4 Modification of the Service</h3>
          <p>
            ViralAI.Tech reserves the right to modify or discontinue the Service at any time
            (including by limiting or discontinuing certain features of the Service), temporarily
            or permanently, without notice to you. ViralAI.Tech will have no liability for any
            change to the Service or any suspension or termination of your access to or use of the
            Service. Premium service fees are not refundable.
          </p>
        </section>

        <section>
          <h2>13. Indemnity</h2>
          <p>
            You are responsible for your use of the Service, and you will defend and indemnify
            ViralAI.Tech and its officers, directors, employees, consultants, affiliates,
            subsidiaries and agents (together, the “ViralAI.Tech Entities”) from and against every
            claim brought by a third party, and any related liability, damage, loss, and expense,
            including reasonable attorneys’ fees and costs, arising out of or connected with: (a)
            your use of, or misuse of, the Service; (b) your violation of any portion of these
            Terms, any representation, warranty, or agreement referenced in these Terms, or any
            applicable law or regulation; (c) your violation of any third party right, including
            any intellectual property right or publicity, confidentiality, other property, or
            privacy right; or (d) any dispute or issue between you and any third party. We reserve
            the right, at our own expense, to assume the exclusive defense and control of any
            matter otherwise subject to indemnification by you (without limiting your
            indemnification obligations with respect to that matter), and in that case, you agree
            to cooperate with our defense of those claims.
          </p>
        </section>

        <section>
          <h2>14. Disclaimers; No Warranties</h2>
          <p>
            THE SERVICE AND ALL MATERIALS AND CONTENT AVAILABLE THROUGH THE SERVICE ARE PROVIDED
            “AS IS” AND ON AN “AS AVAILABLE” BASIS. ViralAI.Tech DISCLAIMS ALL WARRANTIES OF ANY
            KIND, WHETHER EXPRESS OR IMPLIED, RELATING TO THE SERVICE AND ALL MATERIALS AND CONTENT
            AVAILABLE THROUGH THE SERVICE, INCLUDING: (A) ANY IMPLIED WARRANTY OF MERCHANTABILITY,
            FITNESS FOR A PARTICULAR PURPOSE, TITLE, QUIET ENJOYMENT, OR NON-INFRINGEMENT; AND (B)
            ANY WARRANTY ARISING OUT OF COURSE OF DEALING, USAGE, OR TRADE. ViralAI.Tech DOES NOT
            WARRANT THAT THE SERVICE OR ANY PORTION OF THE SERVICE, OR ANY MATERIALS OR CONTENT
            OFFERED THROUGH THE SERVICE, WILL BE UNINTERRUPTED, SECURE, OR FREE OF ERRORS, VIRUSES,
            OR OTHER HARMFUL COMPONENTS, AND ViralAI.Tech DOES NOT WARRANT THAT ANY OF THOSE ISSUES
            WILL BE CORRECTED.
          </p>
          <p>
            NO ADVICE OR INFORMATION, WHETHER ORAL OR WRITTEN, OBTAINED BY YOU FROM THE SERVICE OR
            ViralAI.Tech ENTITIES OR ANY MATERIALS OR CONTENT AVAILABLE THROUGH THE SERVICE WILL
            CREATE ANY WARRANTY REGARDING ANY OF THE ViralAI.Tech ENTITIES OR THE SERVICE THAT IS
            NOT EXPRESSLY STATED IN THESE TERMS. WE ARE NOT RESPONSIBLE FOR ANY DAMAGE THAT MAY
            RESULT FROM THE SERVICE AND YOUR DEALING WITH ANY OTHER SERVICE USER. YOU UNDERSTAND
            AND AGREE THAT YOU USE ANY PORTION OF THE SERVICE AT YOUR OWN DISCRETION AND RISK, AND
            THAT WE ARE NOT RESPONSIBLE FOR ANY DAMAGE TO YOUR PROPERTY (INCLUDING YOUR COMPUTER
            SYSTEM OR MOBILE DEVICE USED IN CONNECTION WITH THE SERVICE) OR ANY LOSS OF DATA,
            INCLUDING USER CONTENT.
          </p>
          <p>
            HOWEVER, ViralAI.Tech DOES NOT DISCLAIM ANY WARRANTY OR OTHER RIGHT THAT ViralAI.Tech
            IS PROHIBITED FROM DISCLAIMING UNDER APPLICABLE LAW.
          </p>
        </section>

        <section>
          <h2>15. Limitation of Liability</h2>
          <p>
            TO THE FULLEST EXTENT PERMITTED BY LAW, IN NO EVENT WILL THE ViralAI.Tech ENTITIES BE
            LIABLE TO YOU FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL OR PUNITIVE DAMAGES
            (INCLUDING DAMAGES FOR LOSS OF PROFITS, GOODWILL, OR ANY OTHER INTANGIBLE LOSS) ARISING
            OUT OF OR RELATING TO YOUR ACCESS TO OR USE OF, OR YOUR INABILITY TO ACCESS OR USE, THE
            SERVICE OR ANY MATERIALS OR CONTENT ON THE SERVICE, WHETHER BASED ON WARRANTY,
            CONTRACT, TORT (INCLUDING NEGLIGENCE), STATUTE, OR ANY OTHER LEGAL THEORY, AND WHETHER
            OR NOT ANY ViralAI.Tech ENTITY HAS BEEN INFORMED OF THE POSSIBILITY OF DAMAGE.
          </p>
          <p>
            EXCEPT AS PROVIDED IN SECTION 16.4(iii) AND TO THE FULLEST EXTENT PERMITTED BY LAW, THE
            AGGREGATE LIABILITY OF THE ViralAI.Tech ENTITIES TO YOU FOR ALL CLAIMS ARISING OUT OF
            OR RELATING TO THE USE OF OR ANY INABILITY TO USE ANY PORTION OF THE SERVICE OR
            OTHERWISE UNDER THESE TERMS, WHETHER IN CONTRACT, TORT, OR OTHERWISE, IS LIMITED TO THE
            GREATER OF: (A) THE AMOUNT YOU HAVE PAID TO ViralAI.Tech FOR ACCESS TO AND USE OF THE
            SERVICE IN THE 12 MONTHS PRIOR TO THE EVENT OR CIRCUMSTANCE GIVING RISE TO CLAIM; OR
            (B) $100.
          </p>
          <p>
            EACH PROVISION OF THESE TERMS THAT PROVIDES FOR A LIMITATION OF LIABILITY, DISCLAIMER
            OF WARRANTIES, OR EXCLUSION OF DAMAGES IS INTENDED TO AND DOES ALLOCATE THE RISKS
            BETWEEN THE PARTIES UNDER THESE TERMS. THIS ALLOCATION IS AN ESSENTIAL ELEMENT OF THE
            BASIS OF THE BARGAIN BETWEEN THE PARTIES. EACH OF THESE PROVISIONS IS SEVERABLE AND
            INDEPENDENT OF ALL OTHER PROVISIONS OF THESE TERMS. THE LIMITATIONS IN THIS SECTION 15
            WILL APPLY EVEN IF ANY LIMITED REMEDY FAILS OF ITS ESSENTIAL PURPOSE.
          </p>
        </section>

        <section>
          <h2>16. Dispute Resolution and Arbitration</h2>

          <h3>16.1 Generally</h3>
          <p>
            In the interest of resolving disputes between you and ViralAI.Tech in the most
            expedient and cost effective manner, and except as described in Section 16.2, you and
            ViralAI.Tech agree that every dispute arising in connection with these Terms will be
            resolved by binding arbitration. Arbitration is less formal than a lawsuit in court.
            Arbitration uses a neutral arbitrator instead of a judge or jury, may allow for more
            limited discovery than in court, and can be subject to very limited review by courts.
            Arbitrators can award the same damages and relief that a court can award. This
            agreement to arbitrate disputes includes all claims arising out of or relating to any
            aspect of these Terms, whether based in contract, tort, statute, fraud,
            misrepresentation, or any other legal theory, and regardless of whether a claim arises
            during or after the termination of these Terms. YOU UNDERSTAND AND AGREE THAT, BY
            ENTERING INTO THESE TERMS, YOU AND ViralAI.Tech ARE EACH WAIVING THE RIGHT TO A TRIAL
            BY JURY OR TO PARTICIPATE IN A CLASS ACTION.
          </p>

          <h3>16.2 Exceptions</h3>
          <p>
            Despite the provisions of Section 16.1, nothing in these Terms will be deemed to waive,
            preclude, or otherwise limit the right of either party to: (a) bring an individual
            action in small claims court; (b) pursue an enforcement action through the applicable
            federal, state, or local agency if that action is available; (c) seek injunctive relief
            in a court of law in aid of arbitration; or (d) to file suit in a court of law to
            address an intellectual property infringement claim.
          </p>

          <h3>16.3 Arbitrator</h3>
          <p>
            Any arbitration between you and ViralAI.Tech will be settled under the Federal
            Arbitration Act and administered by the American Arbitration Association (“AAA”) under
            its Consumer Arbitration Rules (collectively, “AAA Rules”) as modified by these Terms.
            The AAA Rules and filing forms are available online at www.adr.org, by calling the AAA
            at 1–800–778–7879, or by contacting ViralAI.Tech. The arbitrator has exclusive
            authority to resolve any dispute relating to the interpretation, applicability, or
            enforceability of this binding arbitration agreement.
          </p>

          <h3>16.4 Notice of Arbitration; Process</h3>
          <p>
            A party who intends to seek arbitration must first send a written notice of the dispute
            to the other party by certified U.S. Mail or by Federal Express (signature required)
            or, only if that other party has not provided a current physical address, then by
            electronic mail (“Notice of Arbitration”). ViralAI.Tech’s address for Notice is:
            ViralAI.Tech, 306 Haldeman Dr, Cranberry Township, PA, USA 16066. The Notice of
            Arbitration must: (a) describe the nature and basis of the claim or dispute; and (b)
            set forth the specific relief sought (“Demand”). The parties will make good faith
            efforts to resolve the claim directly, but if the parties do not reach an agreement to
            do so within 30 days after the Notice of Arbitration is received, you or ViralAI.Tech
            may commence an arbitration proceeding. During the arbitration, the amount of any
            settlement offer made by you or ViralAI.Tech must not be disclosed to the arbitrator
            until after the arbitrator makes a final decision and award, if any. If the dispute is
            finally resolved through arbitration in your favor, ViralAI.Tech will pay you the
            highest of the following: (i) the amount awarded by the arbitrator, if any; (ii) the
            last written settlement amount offered by ViralAI.Tech in settlement of the dispute
            prior to the arbitrator’s award; or (iii) $10,000.
          </p>

          <h3>16.5 Fees</h3>
          <p>
            If you commence arbitration in accordance with these Terms, ViralAI.Tech will reimburse
            you for your payment of the filing fee, unless your claim is for more than $10,000, in
            which case the payment of any fees will be decided by the AAA Rules. Any arbitration
            hearing will take place at a location to be agreed upon in Santa Clara County,
            California, but if the claim is for $10,000 or less, you may choose whether the
            arbitration will be conducted: (a) solely on the basis of documents submitted to the
            arbitrator; (b) through a non-appearance based telephone hearing; or (c) by an
            in-person hearing as established by the AAA Rules in the county (or parish) of your
            billing address. If the arbitrator finds that either the substance of your claim or the
            relief sought in the Demand is frivolous or brought for an improper purpose (as
            measured by the standards set forth in Federal Rule of Civil Procedure 11(b)), then the
            payment of all fees will be governed by the AAA Rules. In that case, you agree to
            reimburse ViralAI.Tech for all monies previously disbursed by it that are otherwise
            your obligation to pay under the AAA Rules. Regardless of the manner in which the
            arbitration is conducted, the arbitrator must issue a reasoned written decision
            sufficient to explain the essential findings and conclusions on which the decision and
            award, if any, are based. The arbitrator may make rulings and resolve disputes as to
            the payment and reimbursement of fees or expenses at any time during the proceeding and
            upon request from either party made within 14 days of the arbitrator’s ruling on the
            merits.
          </p>

          <h3>16.6 No Class Actions</h3>
          <p>
            YOU AND ViralAI.Tech AGREE THAT EACH MAY BRING CLAIMS AGAINST THE OTHER ONLY IN YOUR OR
            ITS INDIVIDUAL CAPACITY AND NOT AS A PLAINTIFF OR CLASS MEMBER IN ANY PURPORTED CLASS OR
            REPRESENTATIVE PROCEEDING. Further, unless both you and ViralAI.Tech agree otherwise,
            the arbitrator may not consolidate more than one person’s claims, and may not otherwise
            preside over any form of a representative or class proceeding.
          </p>

          <h3>16.7 Modifications to this Arbitration Provision</h3>
          <p>
            If ViralAI.Tech makes any future change to this arbitration provision, other than a
            change to ViralAI.Tech’s address for Notice of Arbitration, you may reject the change
            by sending us written notice within 30 days of the change to ViralAI.Tech’s address for
            Notice of Arbitration, in which case your account with ViralAI.Tech will be immediately
            terminated and this arbitration provision, as in effect immediately prior to the
            changes you rejected will survive.
          </p>

          <h3>16.8 Enforceability</h3>
          <p>
            If Section 16.6 is found to be unenforceable or if the entirety of this Section 16 is
            found to be unenforceable, then the entirety of this Section 16 will be null and void
            and, in that case, the parties agree that the exclusive jurisdiction and venue
            described in Section 17.1 will govern any action arising out of or related to these
            Terms.
          </p>
        </section>

        <section>
          <h2>17. General Terms</h2>
          <p>
            These Terms, together with the <Link to="/privacy">Privacy Policy</Link> and any other
            agreements expressly incorporated by reference into these Terms, are the entire and
            exclusive understanding and agreement between you and ViralAI.Tech regarding your use
            of the Service. You may not assign or transfer these Terms or your rights under these
            Terms, in whole or in part, by operation of law or otherwise, without our prior written
            consent. We may assign these Terms at any time without notice or consent. The failure
            to require performance of any provision will not affect our right to require
            performance at any other time after that, nor will a waiver by us of any breach or
            default of these Terms, or any provision of these Terms, be a waiver of any subsequent
            breach or default or a waiver of the provision itself. Use of section headers in these
            Terms is for convenience only and will not have any impact on the interpretation of any
            provision. Throughout these Terms the use of the word “including” means “including but
            not limited to”. If any part of these Terms is held to be invalid or unenforceable, the
            unenforceable part will be given effect to the greatest extent possible, and the
            remaining parts will remain in full force and effect.
          </p>

          <h3>17.1 Governing Law</h3>
          <p>
            These Terms are governed by the laws of the State of California without regard to
            conflict of law principles. You and ViralAI.Tech submit to the personal and exclusive
            jurisdiction of the state courts and federal courts located within Santa Clara County,
            California for resolution of any lawsuit or court proceeding permitted under these
            Terms.
          </p>

          <h3>17.2 Consent to Electronic Communications</h3>
          <p>
            By using the Service, you consent to receiving certain electronic communications from
            us as further described in our <Link to="/privacy">Privacy Policy</Link>. Please read
            our Privacy Policy to learn more about our electronic communications practices. You
            agree that any notices, agreements, disclosures, or other communications that we send
            to you electronically will satisfy any legal communication requirements, including that
            those communications be in writing.
          </p>

          <h3>17.3 Contact Information</h3>
          <p>
            The Service is offered by ViralAI.Tech. You may contact us by emailing{' '}
            <a href="mailto:support@vimapi.com">support@vimapi.com</a> or{' '}
            <a href="mailto:creator@viralai.tech">creator@viralai.tech</a>, by messaging us on
            WhatsApp at {whatsappLink}, or through our website at{' '}
            <a href="https://viralai.tech">viralai.tech</a>. You can access a copy of these Terms
            by clicking here: <a href="https://viralai.tech/terms">viralai.tech/terms</a>
          </p>

          <h3>17.4 Notice to California Residents</h3>
          <p>
            If you are a California resident, under California Civil Code Section 1789.3, you may
            contact the Complaint Assistance Unit of the Division of Consumer Services of the
            California Department of Consumer Affairs in writing at 1625 N. Market Blvd., Suite
            S-202, Sacramento, California 95834, or by telephone at (800) 952–5210 in order to
            resolve a complaint regarding the Service or to receive further information regarding
            use of the Service.
          </p>

          <h3>17.5 International Use</h3>
          <p>
            Access to the Service from countries or territories or by individuals where such access
            is illegal is prohibited
          </p>
        </section>

        <footer className="legal-footer">
          <p><strong>Last Updated:</strong> September 9, 2026</p>
          <p>
            By using ViralAI, you acknowledge that you have read, understood, and agree to be bound by these
            Terms and Conditions.
          </p>
        </footer>
      </div>
    </div>
  );
};

export default Terms;
